/**
 * Phrases with anchors (1.O-1 deliverable 2), pure. The When input shows `@Label`; each page the
 * GM picked is kept as an `AnchorToken` at its offset, and `serializePhrase` writes the token as
 * `@[Label](ref:<fid>)` for 1.K-1's grammar (its Decision 10): the fid, never the uuid.
 */

export interface AnchorToken {
  readonly label: string;
  readonly docId: string;
  readonly fid: string;
  /** The offset of the token's `@` in the visible text. */
  readonly start: number;
}

/** What a `search.picker` hit gives an anchor (`DocHitV1`). */
export interface AnchorHit {
  readonly id: string;
  readonly fid: string;
  readonly title: string;
}

const tokenText = (token: AnchorToken) => `@${token.label}`;
const tokenEnd = (token: AnchorToken) => token.start + tokenText(token).length;

/** The tokens whose text still stands where they say, in offset order, overlaps dropped. */
export function liveTokens(text: string, anchors: readonly AnchorToken[]): AnchorToken[] {
  const sorted = [...anchors].sort((a, b) => a.start - b.start);
  const kept: AnchorToken[] = [];
  for (const token of sorted) {
    const previous = kept[kept.length - 1];
    if (text.slice(token.start, tokenEnd(token)) !== tokenText(token)) continue;
    if (previous !== undefined && tokenEnd(previous) > token.start) continue;
    kept.push(token);
  }
  return kept;
}

/** A label the grammar's bracket form can carry (1.K-1 Decision 10). */
function writable(token: AnchorToken): boolean {
  return token.label.trim() !== '' && !token.label.includes(']') && !token.fid.includes(')');
}

/** The phrase as 1.K-1's `parse` reads it: each live token as `@[Label](ref:<fid>)`. */
export function serializePhrase(text: string, anchors: readonly AnchorToken[]): string {
  let out = '';
  let at = 0;
  for (const token of liveTokens(text, anchors)) {
    if (!writable(token)) continue;
    out += `${text.slice(at, token.start)}@[${token.label}](ref:${token.fid})`;
    at = tokenEnd(token);
  }
  return out + text.slice(at);
}

/** The page a parsed anchor's fid stands for, or null. */
export function docIdForFid(anchors: readonly AnchorToken[], fid: string | null): string | null {
  if (fid === null) return null;
  return anchors.find((token) => token.fid === fid)?.docId ?? null;
}

export interface ActiveMention {
  /** The offset of the `@`. */
  readonly start: number;
  readonly query: string;
}

const MAX_QUERY = 60;

/**
 * The `@` query under the caret: the text from an `@` that starts a word up to the caret, when
 * that `@` is no token's own and the query holds no line break and does not start with a space.
 */
export function activeMention(
  text: string,
  caret: number,
  anchors: readonly AnchorToken[] = [],
): ActiveMention | null {
  const before = text.slice(0, caret);
  const start = before.lastIndexOf('@');
  if (start === -1) return null;
  if (start > 0 && !/\s/.test(before.charAt(start - 1))) return null;
  const query = before.slice(start + 1);
  if (query.length > MAX_QUERY || /^\s|\n/.test(query)) return null;
  const owned = liveTokens(text, anchors).some((t) => t.start <= start && start < tokenEnd(t));
  return owned ? null : { start, query };
}

export interface Inserted {
  readonly text: string;
  readonly caret: number;
  readonly anchors: AnchorToken[];
}

/** Replaces the active `@` query with the picked page's token; the caret lands after it. */
export function insertAnchor(
  text: string,
  caret: number,
  hit: AnchorHit,
  anchors: readonly AnchorToken[] = [],
): Inserted {
  const mention = activeMention(text, caret, anchors) ?? { start: caret, query: '' };
  const token: AnchorToken = {
    label: hit.title,
    docId: hit.id,
    fid: hit.fid,
    start: mention.start,
  };
  const next = `${text.slice(0, mention.start)}${tokenText(token)}${text.slice(caret)}`;
  const kept = shiftAnchors(text, next, anchors);
  return {
    text: next,
    caret: tokenEnd(token),
    anchors: liveTokens(next, [...kept, token]),
  };
}

/**
 * The tokens after an edit from `previous` to `next`: those wholly before the changed stretch
 * stay, those wholly after it move with it, and a token the edit touched is dropped.
 */
export function shiftAnchors(
  previous: string,
  next: string,
  anchors: readonly AnchorToken[],
): AnchorToken[] {
  const max = Math.min(previous.length, next.length);
  let head = 0;
  while (head < max && previous.charAt(head) === next.charAt(head)) head++;
  let tail = 0;
  while (
    tail < max - head &&
    previous.charAt(previous.length - 1 - tail) === next.charAt(next.length - 1 - tail)
  ) {
    tail++;
  }
  const changedEnd = previous.length - tail;
  const delta = next.length - previous.length;
  const out: AnchorToken[] = [];
  for (const token of liveTokens(previous, anchors)) {
    if (tokenEnd(token) <= head) out.push(token);
    else if (token.start >= changedEnd) out.push({ ...token, start: token.start + delta });
  }
  return liveTokens(next, out);
}
